/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, Mail, ArrowUpRight, Linkedin, Youtube } from "lucide-react";

const XLogo = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const audiences = [
  "A Corporate, HNI or NRI needing tax optimisation & structuring",
  "An Individual needing ITR, GST or tax planning",
  "A CA or tax practitioner exploring TaxSetu.ai or PractiQ",
  "An aspiring tax professional needing career-ready training",
  "Looking for a corporate speaker or visiting faculty",
];

const socials = [
  { icon: Linkedin, href: "https://www.linkedin.com/in/cashivangimittal/", label: "LinkedIn", testid: "contact-social-linkedin" },
  { icon: XLogo, href: "https://x.com/CAShivangi_m", label: "X", testid: "contact-social-twitter" },
  { icon: Youtube, href: "https://www.youtube.com/@CAShivangiMittal", label: "YouTube", testid: "contact-social-youtube" },
];

const EMAIL = "camittal.shivangi@gmail.com";

export const Contact = () => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      // clipboard not available — fall through
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative section-pad overflow-hidden">
      <div className="shape-blob w-[520px] h-[520px] -right-40 -top-20 opacity-30"
           style={{ background: "radial-gradient(circle, #16D1C8 0%, transparent 70%)" }} />
      <div className="shape-blob w-[520px] h-[520px] -left-40 bottom-0 opacity-30"
           style={{ background: "radial-gradient(circle, #FF2D7A 0%, transparent 70%)" }} />

      <div className="container-x relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="label-eyebrow mb-5">Let's connect</div>
          <h2 data-testid="contact-title" className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Reach out. <br />
            <span className="gradient-text">I read every message.</span>
          </h2>
          <p className="mt-6 text-white/65 max-w-2xl mx-auto">
            Whether you need a tax strategist, a product partner, or a speaker for your next session — drop a note.
          </p>
        </motion.div>

        {/* Email card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mt-14 max-w-3xl mx-auto"
        >
          <div className="glass p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-30 blur-3xl"
                 style={{ background: "linear-gradient(135deg, #16D1C8, #FF2D7A)" }} />

            <div className="relative z-10 text-center">
              <div className="flex justify-center">
                <div className="w-14 h-14 rounded-2xl grid place-items-center"
                     style={{ background: "linear-gradient(135deg, #16D1C8, #FF2D7A)" }}>
                  <Mail size={22} className="text-ink-800" />
                </div>
              </div>
              <div className="mt-5 text-xs uppercase tracking-[0.3em] text-teal-300">Drop me a line</div>
              <a
                data-testid="contact-email-link"
                href={`mailto:${EMAIL}`}
                className="block mt-3 font-heading font-semibold text-2xl sm:text-3xl md:text-4xl text-white hover:text-teal-300 transition-colors whitespace-nowrap overflow-x-auto"
              >
                {EMAIL}
              </a>
              <div className="mt-7 flex flex-wrap justify-center items-center gap-3">
                <button
                  data-testid="contact-copy-email"
                  onClick={copy}
                  className="btn-outline !py-3"
                >
                  {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy email</>}
                </button>
                <a
                  data-testid="contact-email-cta"
                  href={`mailto:${EMAIL}`}
                  className="btn-primary !py-3"
                >
                  Email me <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Audiences */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-12 max-w-3xl mx-auto"
        >
          <div className="text-center text-sm uppercase tracking-[0.25em] text-white/40 mb-6">
            Connect if you are
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {audiences.map((a, i) => (
              <li
                key={a}
                data-testid={`audience-${i}`}
                className="glass p-4 flex items-start gap-3 text-sm text-white/80"
              >
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ background: i % 2 === 0 ? "#16D1C8" : "#FF2D7A" }} />
                <span>{a}</span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* Socials */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 flex justify-center items-center gap-3"
          data-testid="contact-socials"
        >
          {socials.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={s.testid}
              aria-label={s.label}
              className="w-12 h-12 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-white hover:border-teal-400/60 hover:bg-teal-400/5 transition-all"
            >
              <s.icon size={16} />
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
